import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Bugsnag from '@bugsnag/js';
import loadable from '@loadable/component';
import styled from 'styled-components';
import {
    Button,
    Divider,
    ErrorAlert,
    Input,
    Link,
    ProgressBar,
    Stack,
    useFloatingAlert,
} from '@99designs/emu';
import { colors, typography } from '@99designs/emu/settings';
import { FormattedMessage, FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { linkTo } from '@99designs/routing';
import { PASSWORD_MIN, PASSWORD_MIN_ADMIN, Source } from '../../common/constants';
import is99StaffEmail from '../../common/is99StaffEmail';
import { linkTo as deprecatedLinkTo } from '../../common/linkTo';
import { partitionArray } from '../../common/partitionArray';
import { PageType } from '../../common/types';
import { BackendErrorType, getErrorForField, setFieldErrors } from '../../common/utils';
import { useAuthConfigContext } from '../AuthConfigContext';
import { AuthLink } from '../AuthLink';
import { Captcha } from '../Captcha';
import { PasswordField, Radio } from '../Form';
import { Layout } from '../Layout';
import { OAuthContinueButton } from '../OAuthContinueButton';

const PasswordStrengthBar = loadable(() => import('../PasswordStrengthBar'), {
    resolveComponent: (components) => components.PasswordStrengthBar,
});

export interface SignUpProps {
    email?: string;
    hideRoleSelector?: boolean;
    onSubmit: (
        endpoint: 'login' | 'signup',
        data: Record<string, unknown>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) => Promise<{ status: number; body: any }>;
    setPage: (page: PageType, link: string) => void;
    showRecaptcha?: boolean;
    source: Source;
    target?: string;
    userRole?: 'client' | 'designer';
}

interface FormState {
    email: string;
    password: string;
    radio_user: string;
    confirmPassword: string;
}

const PuntRoadSubtleText = styled.small`
    color: ${colors.grey800};
    font-size: ${typography.typeScale.size2}px;
`;

export function SignUp({
    email = '',
    hideRoleSelector = false,
    onSubmit,
    setPage,
    showRecaptcha = true,
    source,
    target,
    userRole = 'client',
}: SignUpProps) {
    const { recaptchaSiteKey, locale } = useAuthConfigContext();
    const [recaptchaError, setRecaptchaError] = useState('');
    const [authError, setAuthError] = useState<string>('');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const recaptchaRef = useRef<any>(null);
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const {
        register,
        handleSubmit,
        watch,
        setError,
        formState: { errors, isSubmitting },
        getValues,
    } = useForm<FormState>({
        defaultValues: {
            email,
            password: '',
            confirmPassword: '',
            radio_user: userRole,
        },
    });

    const { ref: rhfEmailRef, ...rhfEmailProps } = register('email', {
        required: __('Enter your email address'),
    });
    const emailRef = useRef<HTMLInputElement | null>();
    useEffect(() => {
        if (emailRef.current) {
            emailRef.current.focus();
        }
    }, []);

    const floatingAlert = useFloatingAlert();

    const currentPassword = watch('password');
    const currentEmail = watch('email');
    const currentRole = watch('radio_user');

    function handleRecaptchaChange(token: string) {
        setRecaptchaError('');
        setRecaptchaToken(token);
    }

    function handleDismissError() {
        setAuthError('');
    }

    function submitForm({ email, password, radio_user }: FormState) {
        const needsRecaptcha = showRecaptcha && !recaptchaToken;
        if (recaptchaSiteKey && needsRecaptcha) {
            setRecaptchaError(__("Confirm that you're not a robot"));
            return;
        }

        return onSubmit('signup', {
            email,
            password,
            role: radio_user || currentRole,
            recaptchaToken,
        })
            .then((d) => {
                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }

                if (d.body.errors) {
                    const errors = partitionArray(d.body.errors, (error: BackendErrorType) => {
                        return Object.keys(getValues()).includes(error.param);
                    });

                    errors.nonMatches.forEach((item: BackendErrorType) => {
                        switch (item.param) {
                            case 'captcha':
                                setRecaptchaError(item.message);
                                break;
                            case '*':
                                setAuthError(item.message);
                                break;
                            default:
                                floatingAlert({
                                    type: 'error',
                                    message: __('Something went wrong, please try again later.'),
                                });
                                Bugsnag.notify(`${item.param} : ${item.message}`, (event) => {
                                    event.context = 'Signing up';
                                });
                                break;
                        }
                    });

                    setFieldErrors(errors.matches, setError);
                    return;
                }
            })
            .catch((e) => {
                Bugsnag.notify(e, (event) => {
                    event.context = 'Signing up';
                });
            });
    }

    return (
        <Layout
            title={__('Sign up')}
            description={__('Sign up for a 99designs account')}
            heading={__('Sign up')}
        >
            <form onSubmit={handleSubmit(submitForm)}>
                <Stack space="1.5bu">
                    {!!authError && (
                        <ErrorAlert onDismiss={handleDismissError}>{authError}</ErrorAlert>
                    )}
                    {!hideRoleSelector && (
                        <Stack space="0.5bu">
                            <Radio
                                {...register('radio_user')}
                                id="radio_client"
                                value="client"
                                defaultChecked={currentRole === 'client'}
                                data-testid="signup-client"
                            >
                                {__('I need something designed')}
                            </Radio>
                            <Radio
                                {...register('radio_user')}
                                id="radio_designer"
                                value="designer"
                                defaultChecked={currentRole === 'designer'}
                                data-testid="signup-designer"
                            >
                                {__("I'm a designer")}
                            </Radio>
                        </Stack>
                    )}

                    <Input
                        {...rhfEmailProps}
                        key="email"
                        error={getErrorForField('email', errors)}
                        id="email"
                        type="text"
                        label={__('Email')}
                        ref={(el) => {
                            rhfEmailRef(el);
                            emailRef.current = el;
                        }}
                        data-testid="signup-email"
                        autoFocus
                        autoComplete="username"
                    />

                    <Stack space="0.5bu">
                        <PasswordField
                            {...register('password', {
                                required: __('Enter your password'),
                            })}
                            key="password"
                            error={getErrorForField('password', errors)}
                            id="password"
                            label={__('Password')}
                            data-testid="signup-password"
                            autoComplete="new-password"
                        />

                        <PasswordStrengthBar
                            password={currentPassword}
                            // eslint-disable-next-line @nrwl/nx/workspace/ui-progressbar-require-a11y-name
                            fallback={<ProgressBar progress={0} small />}
                        />
                        <PuntRoadSubtleText>
                            <FormattedMessage
                                message={__(
                                    'Use {minChars} or more characters with a mix of letters, numbers and symbols'
                                )}
                                values={{
                                    minChars: is99StaffEmail(currentEmail)
                                        ? PASSWORD_MIN_ADMIN
                                        : PASSWORD_MIN,
                                }}
                            />
                        </PuntRoadSubtleText>
                    </Stack>

                    {showRecaptcha && !!recaptchaSiteKey && (
                        <Captcha
                            ref={recaptchaRef}
                            siteKey={recaptchaSiteKey}
                            onChange={handleRecaptchaChange}
                            error={recaptchaError || getErrorForField('captcha', errors)}
                        />
                    )}

                    <PuntRoadSubtleText>
                        <FormattedReactMessage
                            message={__(
                                'By signing up, you agree to our <Link1>Terms of Service</Link1> and have read and acknowledge the <Link2>Privacy Policy</Link2>'
                            )}
                            replacements={{
                                Link1: <Link href={deprecatedLinkTo.termsOfUse()} size="size2" />,
                                Link2: (
                                    <Link href={deprecatedLinkTo.privacyPolicy()} size="size2" />
                                ),
                            }}
                        />
                    </PuntRoadSubtleText>

                    <PuntRoadSubtleText>
                        <FormattedReactMessage
                            message={__(
                                'An update to our <Link1>Terms and Conditions</Link1> and <Link2>Privacy and Cookie Policy</Link2> will be effective on {date}.'
                            )}
                            values={{
                                date: '2022/09/28',
                            }}
                            replacements={{
                                Link1: (
                                    <Link
                                        href={linkTo.vistaUrlByLocale(
                                            locale,
                                            '/terms-and-conditions-220928/'
                                        )}
                                        size="size2"
                                        target={'_blank'}
                                    />
                                ),
                                Link2: (
                                    <Link
                                        href={linkTo.vistaUrlByLocale(
                                            locale,
                                            '/privacy-and-cookie-policy-220928/'
                                        )}
                                        size="size2"
                                        target={'_blank'}
                                    />
                                ),
                            }}
                        />
                    </PuntRoadSubtleText>

                    <Button
                        appearance="primary"
                        width="fill"
                        size="large"
                        loading={isSubmitting}
                        data-testid="signup-button"
                        type="submit"
                    >
                        {__('Sign up')}
                    </Button>

                    <FormattedReactMessage
                        message={__('Already have an account? <Link>Log in</Link>')}
                        replacements={{
                            Link: (
                                <AuthLink
                                    href={deprecatedLinkTo.login(target)}
                                    page="login"
                                    setPage={setPage}
                                />
                            ),
                        }}
                    />

                    {currentRole === 'client' && (
                        <>
                            <Divider />
                            <OAuthContinueButton
                                target={target}
                                action="signup"
                                provider="Vistaprint"
                                source={source}
                            />
                        </>
                    )}
                </Stack>
            </form>
        </Layout>
    );
}
